import React, { useState, useEffect } from 'react'
import { RefreshCw, Shield, CheckCircle, XCircle, Clock, TrendingUp, Activity } from 'lucide-react'

interface CaptchaStats {
  totalVerifications: number
  successfulVerifications: number
  failedVerifications: number
  averageScore: number
  averageResponseTime: number
  errorRate: number
  lastVerificationTime: Date | null
}

interface RolloutStats {
  rolloutPercentage: number
  rolloutEnabled: boolean
  totalRequests: number
  captchaEnabledRequests: number
  rolloutRate: number
}

const CaptchaStatisticsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<CaptchaStats | null>(null)
  const [rolloutStats, setRolloutStats] = useState<RolloutStats | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadStats = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load CAPTCHA stats (when API is ready)
      // For now, use mock data
      const mockStats: CaptchaStats = {
        totalVerifications: 1250,
        successfulVerifications: 1195,
        failedVerifications: 55,
        averageScore: 0.87,
        averageResponseTime: 245,
        errorRate: 4.4,
        lastVerificationTime: new Date()
      }

      const mockRolloutStats: RolloutStats = {
        rolloutPercentage: 25,
        rolloutEnabled: true,
        totalRequests: 1250,
        captchaEnabledRequests: 312,
        rolloutRate: 25.0
      }

      setStats(mockStats)
      setRolloutStats(mockRolloutStats)

      // TODO: Replace with real API calls when backend is ready
      /*
      const [captchaStats, rolloutStats] = await Promise.all([
        adminSettingsApi.getCaptchaStats(),
        adminSettingsApi.getCaptchaRolloutStats()
      ])

      setStats(captchaStats)
      setRolloutStats(rolloutStats)
      */

    } catch (err: any) {
      console.error('Failed to load CAPTCHA statistics:', err)
      setError('Failed to load CAPTCHA statistics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStats()
  }, [])

  const refreshStats = () => {
    loadStats()
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading CAPTCHA statistics...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <XCircle className="h-8 w-8 text-red-500" />
          <span className="ml-2 text-red-700">{error}</span>
        </div>
      </div>
    )
  }

  if (!stats || !rolloutStats) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <Shield className="h-8 w-8 text-gray-400" />
          <span className="ml-2 text-gray-600">No CAPTCHA statistics available</span>
        </div>
      </div>
    )
  }

  const successRate = (stats.successfulVerifications / stats.totalVerifications) * 100
  const isHealthy = stats.errorRate < 5 && successRate > 95

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Shield className="h-6 w-6 text-red-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">CAPTCHA Statistics</h3>
            <p className="text-gray-600 text-sm">Real-time performance and security metrics</p>
          </div>
        </div>
        <button
          onClick={refreshStats}
          className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Status Banner */}
      <div className={`mb-6 p-4 rounded-lg ${isHealthy ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className="flex items-center space-x-2">
          {isHealthy ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-yellow-600" />
          )}
          <div>
            <p className={`font-medium ${isHealthy ? 'text-green-800' : 'text-yellow-800'}`}>
              {isHealthy ? 'CAPTCHA System Healthy' : 'CAPTCHA System Needs Attention'}
            </p>
            <p className={`text-sm ${isHealthy ? 'text-green-700' : 'text-yellow-700'}`}>
              {isHealthy
                ? 'All metrics within acceptable ranges'
                : 'Check error rates and response times'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Verifications */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Verifications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalVerifications.toLocaleString()}</p>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        {/* Success Rate */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{successRate.toFixed(1)}%</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>

        {/* Average Score */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Score</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageScore.toFixed(2)}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        {/* Response Time */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Response</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageResponseTime}ms</p>
            </div>
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Metrics */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Performance Metrics</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Successful Verifications</span>
              <span className="text-sm font-medium text-green-600">{stats.successfulVerifications.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Failed Verifications</span>
              <span className="text-sm font-medium text-red-600">{stats.failedVerifications.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Error Rate</span>
              <span className="text-sm font-medium text-gray-900">{stats.errorRate.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Last Verification</span>
              <span className="text-sm font-medium text-gray-900">
                {stats.lastVerificationTime ? stats.lastVerificationTime.toLocaleTimeString() : 'Never'}
              </span>
            </div>
          </div>
        </div>

        {/* Rollout Metrics */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Rollout Metrics</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Rollout Enabled</span>
              <span className={`text-sm font-medium ${rolloutStats.rolloutEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                {rolloutStats.rolloutEnabled ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Target Percentage</span>
              <span className="text-sm font-medium text-gray-900">{rolloutStats.rolloutPercentage}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Actual Rollout Rate</span>
              <span className="text-sm font-medium text-gray-900">{rolloutStats.rolloutRate.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">CAPTCHA Requests</span>
              <span className="text-sm font-medium text-blue-600">{rolloutStats.captchaEnabledRequests.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Requests</span>
              <span className="text-sm font-medium text-gray-900">{rolloutStats.totalRequests.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-md font-semibold text-blue-900 mb-2">Recommendations</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          {stats.errorRate > 5 && (
            <li>• Consider increasing timeout or checking CAPTCHA service status</li>
          )}
          {successRate < 95 && (
            <li>• Review minimum score threshold - may be too strict</li>
          )}
          {stats.averageResponseTime > 500 && (
            <li>• High response time detected - monitor CAPTCHA service performance</li>
          )}
          {Math.abs(rolloutStats.rolloutRate - rolloutStats.rolloutPercentage) > 5 && (
            <li>• Rollout rate differs from target - check distribution logic</li>
          )}
          <li>• Regularly review failed verifications for potential false positives</li>
          <li>• Consider A/B testing different score thresholds for optimal UX</li>
        </ul>
      </div>
    </div>
  )
}

export default CaptchaStatisticsPanel
